import React, { useEffect, useState } from 'react';
import { useThemesContext } from '../../../../../../builder/context/ThemeProvider';
import { Modal, Button } from "components";
import { ToastWarning } from "../../../../../layouts/dashboard/sidebar/icons/icons/icons";
import {
    ZipIcon,
    CopyJobIcon,
    AssetsIcon,
    BlocksIcon,
    CheckIcon,
    LayoutsIcon,
    LanguagesIcon,
    SectionsIcon,
    SnippetsIcon,
    TemplateIcon,
    PagesIcon,
    MenusIcon,
    SuccessIcon,
    NoLuckIcon,
    FilesIcon,
    SettingsIcon
} from "../../icons";

import "./index.scss";


const CONFIG = {
    duplicate: {
        QUEUED: {
            title: "Theme duplication queued",
            description: "Your duplication request is queued, it will be processed soon.",
        },
        IN_PROGRESS: {
            title: "Theme duplication in progress",
            description: "We are duplicating your theme, this action may take up to 5 minutes.",
        },
        COMPLETED: {
            title: "Theme duplicated",
            description: "We have successfully duplicated your theme, you can now see it in your themes dashboard.",
        },
        ABORTED: {
            title: "Theme duplication aborted",
            description: "Your theme duplication was aborted."
        }
    },
    import: {
        UPLOADING: {
            title: "Your file is loading",
            description: "Your file is being uploaded to the server, this may take some time."
        },
        QUEUED: {
            title: "Theme import queued",
            description: "Your import request is queued, it will be processed soon."
        },
        IN_PROGRESS: {
            title: "Theme import in progress",
            description: "We are importing your theme, this action may take up to 5 minutes."
        },
        COMPLETED: {
            title: "Theme imported",
            description: "We have successfully imported your theme, you can now see it in your themes dashboard. You can also set your imported theme as active and it will be displayed in your storefront page automatically."
        },
        ABORTED: {
            title: "Theme import aborted",
            description: "Your theme import was aborted."
        }
    },
    update: {
        QUEUED: {
            title: "Theme update queued",
            description: "Your update request is queued, it will be processed soon."
        },
        IN_PROGRESS: {
            title: "Theme update in progress",
            description: "We are updating your theme, this action may take up to 5 minutes."
        },
        COMPLETED: {
            title: "Your theme updated succeeded.",
            description: "We have successfully updated your theme, you can now see it in your themes dashboard. Please keep in mind that after an update we create an additional theme (so you do not lose changes), you can set it as active and it will be displayed in your storefront page automatically."
        },
        ABORTED: {
            title: "Theme update aborted",
            description: "Your theme update was aborted."
        }
    },
    clone: {
        QUEUED: {
            title: "Theme clone base queued",
            description: "Your clone base request is queued, it will be processed soon."
        },
        IN_PROGRESS: {
            title: "Theme clone base in progress",
            description: "We are cloning the base of your theme, this action may take up to 5 minutes."
        },
        COMPLETED: {
            title: "Theme base cloned",
            description: "We have successfully cloned the base of your theme, you can now see it in your themes dashboard. You can set it as active and it will be displayed in your storefront page automatically."
        },
        ABORTED: {
            title: "Theme clone base aborted",
            description: "Your theme clone base was aborted."
        }
    },
    create: {
        QUEUED: {
            title: "Theme add to library queue",
            description: "Your creation request is queued, it will be processed soon."
        },
        IN_PROGRESS: {
            title: "Theme add to library in progress",
            description: "We are creating your theme, this action may take up to 5 minutes."
        },
        COMPLETED: {
            title: "Theme base created",
            description: "We have successfully created your theme and added it to your library, you can now see it in your themes dashboard. You can set it as active and it will be displayed in your storefront page automatically."
        },
        ABORTED: {
            title: "Theme add to library aborted",
            description: "Your theme creation was aborted."
        }
    }
}

const Index = ({ theme, openConflicts }) => {

    const { updateTheme, upgradeJob, createTheme, importTheme, createJob, duplicateJob, duplicateTheme, upgradeTheme, jobStatus, themes, terminateJob } = useThemesContext();
    const [warningModal, showWarningModal] = useState(false);

    let isImport = jobStatus.type === "import";

    let isUploading = jobStatus.status === "UPLOADING";
    let isQueued = jobStatus.status === "QUEUED";
    let isInProgress = jobStatus.status === "IN_PROGRESS";
    let isCompleted = jobStatus.status === "COMPLETED";
    let isAborted = jobStatus.status === "ABORTED";

    useEffect(() => {

        let interval = null;

        if (jobStatus) {
            if (isQueued || isInProgress) {

                interval = setInterval(() => {

                    if (jobStatus.type === 'duplicate') {
                        duplicateJob();
                    } else if (jobStatus.type === 'update') {
                        upgradeJob();
                    } else if (jobStatus.type === 'clone' || jobStatus.type === 'create') {
                        createJob(null, jobStatus.type);
                    } else if (jobStatus.type === 'import') {
                        createJob(null, 'import');
                    }

                }, 1500);

            } else {
                clearInterval(interval);
            }
        } else {
            clearInterval(interval)
        }

        return () => {
            clearInterval(interval)
        }

    }, [jobStatus, isQueued, isInProgress, isCompleted, isAborted, isUploading]);

    let newTheme = isCompleted && themes.find(({id}) => id === jobStatus.new_theme_id) ? themes.find(({id}) => id === jobStatus.new_theme_id) : null;

    let currentTitle = null;
    let icon = null;
    let progress = 8;
    let currentDescription = null;

    if (newTheme?.has_update_conflicts && openConflicts) {
        openConflicts(newTheme);
        terminateJob(null);
    }

    if (jobStatus) {
        switch (jobStatus.substatus) {
            case "CLONING_THEME":
                icon = <CopyJobIcon/>;
                currentTitle = jobStatus.type === "create" ? "Creating and adding theme to your library" : "Cloning Theme";
                currentDescription = jobStatus.type === "create" ? "We are adding the theme to your library. To do so, we will first create it and then assign it to your store." : "We are cloning your theme in order to apply the updates to it. This way, should something go wrong, your store will keep working anyway.";
                progress = 13;
                break;
            case "CHECKING_ZIP":
                icon = <ZipIcon/>;
                currentTitle = "Checking Zip";
                currentDescription = "For security reasons, we are validating every file of the theme. This action might take a couple of minutes, depending on the zip.";
                progress = 13;
                break;
            case "CREATING_SETTINGS":
                icon = <SettingsIcon />;
                currentTitle = "Creating Settings";
                currentDescription = "We are creating the settings for your theme, they will define default configurations and options for it.";
                progress = 20;
                break;
            case "CREATING_ASSETS":
                icon = <AssetsIcon/>;
                currentTitle = "Creating Assets";
                currentDescription = "Assets are images and files used on your pages.";
                progress = 24;
                break;
            case "CREATING_BLOCKS":
                icon = <BlocksIcon/>;
                currentTitle = "Creating Blocks";
                currentDescription = "Blocks are used to group elements of your theme.";
                progress = 32;
                break;
            case "CREATING_LAYOUTS":
                icon = <LayoutsIcon/>;
                currentTitle = "Creating Layouts";
                currentDescription = "Layouts are the bigger parts of your theme, they include sections, blocks and everything in between.";
                progress = 40;
                break;
            case "CREATING_LOCALES":
                icon = <LanguagesIcon/>;
                currentTitle = "Creating Locales";
                currentDescription = "With the default being english, locales are used to provide your clients with multiple languages to choose from.";
                progress = 48;
                break;
            case "CREATING_SECTIONS":
                icon = <SectionsIcon/>;
                currentTitle = "Creating Sections";
                currentDescription = "Sections contains blocks and are used to divide your page into multiple parts.";
                progress = 56;
                break;
            case "CREATING_SNIPPETS":
                icon = <SnippetsIcon/>;
                currentTitle = "Creating Snippets";
                currentDescription = "Snippets are smaller elements included throughout your theme for additional features.";
                progress = 64;
                break;
            case "CREATING_TEMPLATES":
                icon = <TemplateIcon/>;
                currentTitle = "Creating Templates";
                currentDescription = "Templates are default pages used to render your theme.";
                progress = 72;
                break;
            case "CREATING_PAGES":
                icon = <PagesIcon/>;
                currentTitle = "Creating Pages";
                currentDescription = "Every page defines which URLs can be accessed and which are rendered to specific templates.";
                progress = 80;
                break;
            case "CREATING_MENUS":
                icon = <MenusIcon/>;
                currentTitle = "Creating Menus";
                currentDescription = "Menus are used in your pages to let clients navigate your store.";
                progress = 88;
                break;
            case "UPLOADING_FILES_AND_FINISHING":
                icon = <FilesIcon/>;
                currentTitle = "Uploading Files And Finishing";
                currentDescription = "We are at the final step, the database structure is finished and we are now uploading your files to our CDN.";
                progress = 96;
                break;
            default:
                break;
        }
    }

    if (isCompleted || isAborted) {
        icon = isCompleted ? <SuccessIcon/> : <NoLuckIcon/>;
        currentTitle = CONFIG[jobStatus.type][jobStatus.status].title;
        currentDescription = CONFIG[jobStatus.type][jobStatus.status].description;
    }

    let headerTitle = '';
    let headerDescription = '';

    if (isQueued || isInProgress || isUploading) {
        headerTitle = CONFIG[jobStatus.type][jobStatus.status].title;
        headerDescription = CONFIG[jobStatus.type][jobStatus.status].description;
    }

    const handleClickRetry = () => {
        terminateJob(null);

        if (jobStatus.type === 'duplicated') {
            duplicateTheme(theme?.id);
        } else if (jobStatus.type === 'update') {
            upgradeTheme(theme);
        } else if (jobStatus.type === 'clone' || jobStatus.type === 'create') {
            createTheme(theme.schema || 'default');
        }
    };

    const closeJobModal = () => {
        if (isQueued || isInProgress) {
            showWarningModal(true);
        } else {
            terminateJob(null);
        }
    };

    const closeWarningModal = () => {
        showWarningModal(false);
        terminateJob(null);
    };

    const handleClickSetActive = async (theme) => {
        await updateTheme(theme, theme?.name, true);
        terminateJob(null);
    };

    return (
        <>
            <Modal open={jobStatus} closeModal={closeJobModal}>
                <div className={`paylixecommerce-modal-header theme-status-modal ${isCompleted || isAborted ? 'd-none' : ''}`}>
                    <div className="theme-status-modal-title">{headerTitle}</div>
                    <div className="theme-status-modal-subtitle">{headerDescription}</div>

                    <div className="theme-status-modal-statuses">
                        {isImport ? <div className={`${isInProgress ? 'active' : ''} ${isUploading ? 'current' : ''}`}>
                            <div>{isQueued || isInProgress ? <CheckIcon /> : '0'}{isQueued || isInProgress ? null : <div className="theme-status-modal-statuses-loader" />}</div>
                            <div>Uploading</div>
                        </div> : null}
                        <div className={`${isInProgress ? 'active' : ''} ${isQueued ? 'current' : ''}`}>
                            <div>{(isImport ? !isUploading : true) && isInProgress ? <CheckIcon /> : '1'}{isQueued ? <div className="theme-status-modal-statuses-loader" /> : null}</div>
                            <div>Queued</div>
                        </div>
                        <div className={`${!isQueued && !isInProgress && (isImport ? !isUploading : true) ? 'active' : ''} ${isInProgress ? 'current' : ''}`}>
                            <div>{!isQueued && !isInProgress && (isImport ? !isUploading : true) ? <CheckIcon /> : '2'}{isInProgress ? <div className="theme-status-modal-statuses-loader" /> : null}</div>
                            <div>In-Progress</div>
                        </div>

                        <div>
                            <div>3</div>
                            <div>Completed</div>
                        </div>
                    </div>
                </div>
                <div className={`paylixecommerce-modal-body ${isImport && (isUploading || isQueued) ? 'd-none' : ''}`}>
                    <div className={`theme-status-modal-progress ${isCompleted || isAborted ? 'd-none' : ''}`}>
                        <div style={{ width: `${isQueued ? 8 : progress}%` }}/>
                    </div>
                    <div className={`theme-status-modal-step ${isCompleted || isAborted ? 'mt-1' : ''}`}>
                        <div className={`theme-status-modal-step-icon ${isCompleted || isAborted ? 'mb-3' : ''}`}>
                            {isInProgress ? <div className="theme-status-modal-step-icon-loader" /> : null}
                            {icon}
                        </div>
                        <div className="theme-status-modal-step-title">
                            {currentTitle}
                        </div>
                        <div className="theme-status-modal-step-description">
                            {currentDescription}
                        </div>
                        {isAborted ? <div className="theme-status-modal-step-error">
                            {jobStatus.description}
                        </div> : null}
                    </div>
                </div>
                <div className="paylixecommerce-modal-footer" style={{borderTop: "none"}}>
                    {isCompleted ? <Button className="w-50 mr-3" variant="primary" size="large" onClick={() => handleClickSetActive(newTheme)}>Set as Active</Button> : null}
                    {isAborted && jobStatus.type !== 'import' ? <Button className="w-50 mr-3" variant="primary" size="large" onClick={handleClickRetry}>Try Again</Button> : null}
                    <Button className={isCompleted || isAborted && jobStatus.type !== 'import' ? "w-50" : "w-100"} variant="secondary" size="large" onClick={closeJobModal}>Close</Button>
                </div>
            </Modal>

            <WarningModal isOpen={warningModal} close={closeWarningModal} wait={() => showWarningModal(null)} />
        </>
    );
};

export default Index;



const WarningModal = ({ isOpen, close, wait }) => (
    <Modal open={isOpen} confirm skipClose closeModal={() => {}} overlayClassName="unsaved-overlay">
        <div className="modal-container">
            <div className="modal-icon error">
                <ToastWarning />
            </div>
            <div className="modal-header" style={{textAlign: "center"}}>&nbsp;</div>
            <div className="modal-description">
                If you close this modal, you won’t receive any update on when this action is finished. <br/>
                Please wait up to 5 minutes for the changes to be applied, then refresh the page.
            </div>
            <div className="modal-footer">
                <Button size="large" variant="secondary" onClick={wait}>
                    Wait More
                </Button>
                <Button size="large" variant="warning" onClick={close}>
                    Confirm Cancel
                </Button>
            </div>
        </div>
    </Modal>
)
